const CHART_CATEGORIES = [
    { label: 'Papel',      color: '#378ADD', keywords: ['papel', 'papelao', 'cartao', 'papelão', 'cartão'] },
    { label: 'Metal',      color: '#EF9F27', keywords: ['metal', 'alumin', 'aco', 'aço', 'ferro', 'cobre', 'lata'] },
    { label: 'Plástico',   color: '#E24B4A', keywords: ['plastic', 'plastico', 'plástico', 'pet', 'pead'] },
    { label: 'Vidro',      color: '#639922', keywords: ['vidro'] },
    { label: 'Eletrônico', color: '#7F77DD', keywords: ['eletr', 'pilha', 'bateria', 'lampada', 'lâmpada'] },
    { label: 'Óleo',       color: '#D85A30', keywords: ['oleo', 'óleo', 'oil', 'gordura'] },
    { label: 'Outros',     color: '#888780', keywords: [] },
];

function normalizeCategory(material) {
    const m = material.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    for (const cat of CHART_CATEGORIES.slice(0, -1)) {
        if (cat.keywords.some(k => m.includes(k))) return cat.label;
    }
    return 'Outros';
}

const _defaultState = () => ({
    profile: null,
    address: null,
    points: null,
    balance: null,
    total_weight: null,
    weighings: null,
});

const state = _defaultState();

const mutations = {
    SET_PROFILE(state, profile) {
        state.profile = profile;
    },
    SET_ADDRESS(state, address) {
        state.address = address;
    },
    SET_POINTS(state, points) {
        state.points = points;
    },
    SET_BALANCE(state, balance) {
        state.balance = balance;
    },
    SET_TOTAL_WEIGHT(state, weight) {
        state.total_weight = weight;
    },
    SET_WEIGHINGS(state, weighings) {
        state.weighings = weighings;
    },
    SET_PROFILE_AVATAR(state, avatar) {
        if (state.profile) state.profile = { ...state.profile, avatar };
    },
    RESET_ACCOUNT_STATE(state) {
        Object.assign(state, _defaultState());
    },
};

const getters = {
    get_profile(state) {
        return state.profile;
    },
    get_address(state) {
        return state.address;
    },
    get_full_name(state) {
        if (!state.profile) return null;
        return `${state.profile.first_name} ${state.profile.last_name}`;
    },
    get_points(state) {
        return state.points;
    },
    get_balance(state) {
        return state.balance;
    },
    get_total_weight(state) {
        return state.total_weight;
    },
    get_weighings(state) {
        return state.weighings;
    },
    get_chart_data(state) {
        if (!state.weighings) return null;

        const totals = Object.fromEntries(CHART_CATEGORIES.map(c => [c.label, 0]));

        for (const weighing of state.weighings) {
            for (const item of weighing.items) {
                const cat = normalizeCategory(item.material);
                totals[cat] += item.weights;
            }
        }

        return CHART_CATEGORIES.map(c => ({
            label: c.label,
            value: Math.round(totals[c.label] * 100) / 100,
            color: c.color,
        }));
    },
};

const actions = {
    async fetchProfile({ commit, rootGetters }) {
        const { ct_users } = await import('@/controllers/users');
        const { access_token } = rootGetters['users/get_user_data'];

        const response = await ct_users.getProfile(access_token);
        if (!response.status) throw response.error;

        commit('SET_PROFILE', response.data);
        if (response.data?.address) {
            commit('SET_ADDRESS', response.data.address);
        }

        return response.data;
    },

    async updateProfile({ dispatch, rootGetters }, data) {
        const { ct_users } = await import('@/controllers/users');
        const { access_token } = rootGetters['users/get_user_data'];

        const response = await ct_users.updateProfile(access_token, data);
        if (!response.status) throw response.error;

        await dispatch('fetchProfile');
        return response;
    },

    async updatePix({ dispatch, rootGetters }, pix_key) {
        const { ct_users } = await import('@/controllers/users');
        const { access_token } = rootGetters['users/get_user_data'];

        const response = await ct_users.updatePix(access_token, pix_key);
        if (!response.status) throw response.error;

        await dispatch('fetchProfile');
        return response;
    },

    async setAddress({ commit, rootGetters }, data) {
        const { ct_users } = await import('@/controllers/users');
        const { access_token } = rootGetters['users/get_user_data'];

        const response = await ct_users.setAddress(access_token, data);
        if (!response.status) throw response.error;

        commit('SET_ADDRESS', data);
        return response;
    },

    async uploadAvatar({ commit, rootGetters }, formData) {
        const { ct_users } = await import('@/controllers/users');
        const { access_token } = rootGetters['users/get_user_data'];

        const response = await ct_users.uploadAvatar(access_token, formData);
        if (!response.status) throw response.error;

        commit('SET_PROFILE_AVATAR', response.avatar);
        return response;
    },

    async fetchStats({ commit, rootGetters }) {
        const { ct_users } = await import('@/controllers/users');
        const { access_token } = rootGetters['users/get_user_data'];

        const [pointsRes, walletRes, weighingsRes] = await Promise.all([
            ct_users.getPoints(access_token),
            ct_users.getWallet(access_token),
            ct_users.getWeighings(access_token),
        ]);

        if (pointsRes.status) commit('SET_POINTS', pointsRes.data.total_points);
        if (walletRes.status) commit('SET_BALANCE', walletRes.data.balance);
        if (weighingsRes.status) {
            const data = weighingsRes.data;
            commit('SET_WEIGHINGS', data);
            const total = data.reduce((acc, w) => acc + w.items.reduce((s, i) => s + i.weights, 0), 0);
            commit('SET_TOTAL_WEIGHT', total);
        }
    },
};

const account = {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};

export default account;
